import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '../../components/SEO/SEO';
import Challenge from '../../assets/images/challenge.jpg';
import Mission from '../../assets/images/mission.jpg';
import Accessibility from '../../assets/images/accessibility.jpg';
import './AboutUs.scss';

export const AboutUs = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  // JSON-LD Schema specifically for the About Page Entity
  const aboutSchemaData = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About GrhamDeal',
    url: 'https://grhamdeal.com/aboutus',
    mainEntity: {
      '@type': 'Organization',
      name: 'GrhamDeal',
      alternateName: 'Grham Deal',
      url: 'https://grhamdeal.com/',
      logo: 'https://grhamdeal.com/logo-512.png',
      parentOrganization: {
        '@type': 'Organization',
        name: 'Kaundal Ventures',
      },
      description:
        'GrhamDeal is a rental property platform that helps tenants find rooms, flats, houses and PG accommodations and connect with property owners across India.',
      sameAs: [
        'https://www.instagram.com/grhamdeal/',
        'https://www.facebook.com/profile.php?id=61580425527343',
        'https://www.youtube.com/@GrhamDeal',
      ],
    },
  };

  return (
    <div className="about-us">
      {/* SEO Meta Tags */}
      <SEO
        title="About GrhamDeal | Rental Homes, Rooms & Flats in India"
        description="Learn about GrhamDeal, a rental property platform operated by Kaundal Ventures that helps tenants find rooms, flats, houses and PG accommodations and connect with property owners across India."
        canonical="https://grhamdeal.com/aboutus"
        image="https://grhamdeal.com/logo-512.png"
      />

      {/* JSON-LD Schema */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(aboutSchemaData)}
        </script>
      </Helmet>

      <div className="inner-banner">
        <div className="banner-content">
          <div className="container">
            <div className="row">
              <div className="col-12 animate__animated animate__fadeInDown">
                <h1 className="border-img-one">About GrhamDeal</h1>
                <p>
                  Welcome to <strong>GrhamDeal</strong> (also known as{' '}
                  <strong>Grham Deal</strong>), your premier platform for
                  connecting property owners and tenants seeking furnished or
                  unfurnished rental accommodations across India.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="bg-white">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6 pe-md-5 mb-4 mb-md-0 animate__animated animate__fadeInLeft">
              <div className="about-img rounded-3 overflow-hidden">
                <img
                  src={Challenge}
                  className="img-fluid"
                  alt="GrhamDeal Rental Challenges"
                />
              </div>
            </div>
            <div className="col-md-6 animate__animated animate__fadeInDown">
              <h2>Our Story & Mission</h2>
              <p>
                Operated by <strong>Kaundal Ventures</strong>,{' '}
                <strong>GrhamDeal</strong> was born out of a desire to simplify
                how people find short- and long-term rental housing. We
                understand the friction owners and tenants experience when
                searching for reliable accommodations, and our platform bridges
                that gap seamlessly.
              </p>
              <p>
                Whether you are searching for rooms, flats, PG accommodations,
                or full houses, our goal is to make the rental search simpler
                and more transparent.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-light">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-8 offset-md-2 animate__animated animate__fadeInUp">
              <h2 className="text-center">Our Primary Goal</h2>
              <p className="text-center">
                At <strong>GrhamDeal</strong>, our primary objective is to
                facilitate fast, secure, and transparent connections between
                property owners and tenants. From single rooms to entire villas,
                we streamline the entire rental journey.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6 animate__animated animate__fadeInLeft">
              <h2>Trust & Verification</h2>
              <p>
                Trust and transparency are important to{' '}
                <strong>GrhamDeal</strong>. We provide owner verification and
                listing approval features to help renters make more informed
                decisions when exploring rental properties.
              </p>
            </div>
            <div className="col-md-6 ps-md-5 animate__animated animate__fadeInDown">
              <div className="about-img rounded-3 overflow-hidden">
                <img
                  src={Mission}
                  className="img-fluid"
                  alt="GrhamDeal Mission"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-light">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6 pe-md-5 mb-4 mb-md-0 animate__animated animate__fadeInLeft">
              <div className="about-img rounded-3 overflow-hidden">
                <img
                  src={Accessibility}
                  className="img-fluid"
                  alt="GrhamDeal Accessibility"
                />
              </div>
            </div>
            <div className="col-md-6 animate__animated animate__fadeInDown">
              <h2>Accessibility & Community</h2>
              <p>
                We build tools that empower both sides. Owners get tools to
                manage their property listings and connect with potential
                tenants, while tenants can use search and filtering tools to
                find suitable rental properties.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="container">
          <div className="row">
            <div className="col-md-8 offset-md-2 text-center animate__animated animate__fadeInUp">
              <h2>Join the GrhamDeal Community</h2>
              <p>
                Whether you have a property to list or are looking for your next
                rental home, <strong>GrhamDeal</strong> is here to assist you
                every step of the way.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-light">
        <div className="container">
          <div className="row">
            <div className="col-md-8 offset-md-2 text-center">
              <h2>About the Company</h2>

              <p>
                <strong>GrhamDeal</strong> is operated by{' '}
                <strong>Kaundal Ventures</strong>. The platform is focused on
                making rental property discovery simpler for tenants and helping
                property owners reach people looking for rental homes.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
